import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { formatInZone } from '../../../lib/tripTime';
import { shortChannelLabel } from '../../../lib/broadcastChannels';
import {
  canCancelScheduledBroadcast,
  scheduledAudienceLabel,
  scheduledAudienceNote,
  scheduledStatusMeta,
} from '../../../lib/scheduledBroadcasts';
import { IN_DOUBT_ROW_NOTE, isInDoubtCancel } from '../../../lib/reminderPlan';
import type { GroupScheduledBroadcast } from '../../../types/groups';

/**
 * One scheduled/sent/cancelled/skipped row. Cancel is gated through
 * `canCancelScheduledBroadcast` — `pending` only, never `sending`/`sent`/
 * `failed`/`cancelled`/`skipped` — so a row cannot end up offering a button
 * whose only possible answer is the 409 this whole screen exists to handle
 * gracefully.
 *
 * `skipped` (#394) is an RSVP reminder that resolved to nobody when it fired:
 * shown as a neutral chip with the API's own sentence beneath, never red —
 * "everyone had replied" is the reminder working, not failing. `failed` is the
 * only status that gets the error color.
 *
 * A `cancelled` row that MAY have been sent (the in-doubt marker, or cancelled after an attempt) says so and offers "Reuse", which prefills the Broadcast composer from the row.
 */
export default function ScheduledRow({
  row,
  timeZone,
  onCancel,
  cancelling,
  nested = false,
  onReuse,
}: {
  row: GroupScheduledBroadcast;
  timeZone: string;
  onCancel: (id: string) => void;
  cancelling: boolean;
  /** Drawn inside a plan card: no border of its own. */
  nested?: boolean;
  /** Loads a reminder that may have been sent (cancelled as in doubt) into the Broadcast composer, prefilled. It only opens the composer: nothing is sent. */
  onReuse?: (row: GroupScheduledBroadcast) => void;
}) {
  const meta = scheduledStatusMeta(row.status, row.lastError);
  return (
    <Paper variant={nested ? 'elevation' : 'outlined'} elevation={0} sx={{ p: nested ? 1.5 : 2, borderRadius: 2, bgcolor: nested ? 'transparent' : undefined }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>{row.subject}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {formatInZone(row.sendAt, timeZone)} · {scheduledAudienceLabel(row)} · {scheduledAudienceNote(row)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {row.channels.map(shortChannelLabel).join(' · ')}
          </Typography>
          {row.status === 'failed' && row.lastError ? (
            <Typography variant="caption" color="error" display="block">{row.lastError}</Typography>
          ) : null}
          {isInDoubtCancel(row) ? (
            <Typography variant="caption" color="warning.main" display="block" data-testid="in-doubt-cancelled">{IN_DOUBT_ROW_NOTE}</Typography>
          ) : null}
          {row.status === 'skipped' && row.lastError ? (
            <Typography variant="caption" color="text.secondary" display="block">{row.lastError}</Typography>
          ) : null}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <Chip size="small" label={meta.label} color={meta.color} />
          {onReuse && isInDoubtCancel(row) ? (
            <Button size="small" variant="outlined" onClick={() => onReuse(row)}>Reuse</Button>
          ) : null}
          {canCancelScheduledBroadcast(row) ? (
            <Button size="small" variant="outlined" disabled={cancelling} onClick={() => onCancel(row.id)}>
              {cancelling ? 'Cancelling…' : 'Cancel'}
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
}
